import React, { ReactElement, ReactNode, useMemo, useRef } from 'react';
import { useDebounce } from 'react-use';
import { Handle, NodeProps, Position } from 'reactflow';
import { twJoin, twMerge } from 'tailwind-merge';

import { Button } from '@dbt-labs/sourdough';

import { InteractionDagNode } from '../dag/internalDag';
import { DbtDagNode } from '../dbt-dag';
import {
  MIN_HOVER_SCALE,
  MIN_ZOOM_TO_SHOW,
  useZoomLevelDebounced,
} from '../hooks/useZoomLevel';
import { useDagContext } from '../providers/DagProvider';

const MIN_SELECTED_NODE_SIZE = 1.1;

type ChildrenProps = {
  isHovered: boolean;
  isSelected: boolean;
  shadowClass: string;
  isMagnified: boolean;
  disableMagnifiedNode: boolean;
};

type ChildrenCallback = (props: ChildrenProps) => ReactNode;

type BaseNodeProps<TDbtDagNode extends DbtDagNode> = NodeProps<
  InteractionDagNode<TDbtDagNode>
> & {
  children: ChildrenCallback;
  hoverXOffset?: number;
  hoverYOffset?: number;
  disableHandles?: boolean;
  disableShadow?: boolean;
};

type BaseNodeContainerProps<TDbtDagNode extends DbtDagNode> =
  BaseNodeProps<TDbtDagNode> & {
    zoom: number;
    isHovered: boolean;
    isFocused: boolean;
    updateIsHovered: (isHovered: boolean) => void;
    updateIsFocused: (isFocused: boolean) => void;
  };

interface BaseNodeDisplayProps<TDbtDagNode extends DbtDagNode> {
  children: ChildrenCallback;
  data: InteractionDagNode<TDbtDagNode>;
  showOnHover: boolean;
  isHovered: boolean;
  isTransparent: boolean;
  isSelected: boolean;
  disableShadow: boolean;
  id: string;
  shadowClass: string;
  hoverStyle: React.CSSProperties | undefined;
  isMagnified: boolean;
}

const MIN_OFFSET_SCALE = MIN_ZOOM_TO_SHOW;

const BaseNodeDisplay = <TDbtDagNode extends DbtDagNode>({
  isHovered,
  isSelected,
  showOnHover,
  isTransparent,
  disableShadow,
  id,
  data,
  shadowClass,
  hoverStyle,
  isMagnified,
  children,
}: BaseNodeDisplayProps<TDbtDagNode>) => {
  const dagContext = useDagContext();

  const disableMagnifiedNode = dagContext.disableMagnifiedNode;

  return (
    <div
      style={isHovered ? hoverStyle : undefined}
      {...(!isMagnified && { 'data-testid': id })}
      onClick={(e) => {
        e.stopPropagation();
        dagContext.onNodeClick(e, data);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        dagContext.onNodeDoubleClick(e, data);
      }}
      className={twJoin(
        'pointer-events-auto relative w-full whitespace-nowrap transition-opacity',
        isTransparent && 'opacity-20',
        !isTransparent && 'opacity-100',
        showOnHover && isHovered && 'min-w-fit',
      )}
    >
      <div
        className={twMerge(
          'rounded-md transition-shadow duration-200 motion-reduce:duration-0',
          !disableShadow && shadowClass,
        )}
      >
        {children({
          isHovered,
          isSelected,
          shadowClass,
          isMagnified,
          disableMagnifiedNode,
        })}
      </div>
    </div>
  );
};

/**
 * The base wrapper node type for handling interactions in the DAG.
 */
const BaseNodeContainer = <TDbtDagNode extends DbtDagNode>({
  data,
  disableHandles = false,
  disableShadow = false,
  hoverYOffset = 0,
  hoverXOffset = 0,
  isHovered,
  updateIsFocused,
  updateIsHovered,
  zoom,
  children,
}: BaseNodeContainerProps<TDbtDagNode>) => {
  const { isSelected, isHidden, id } = data;
  const wasSelectedRef = useRef(isSelected);
  wasSelectedRef.current = wasSelectedRef.current || isSelected;

  const scaleNode = Math.max(1.0, MIN_SELECTED_NODE_SIZE / zoom);
  const shouldMagnify = zoom < MIN_HOVER_SCALE;
  const shouldScaleOffset = zoom < MIN_OFFSET_SCALE;

  const isTransparent = isHidden && !isHovered;

  const isHoveredOrSelected = isHovered || isSelected;

  const shadowClass = twMerge(
    !isHoveredOrSelected && !isHidden && 'shadow-node-rest',
    !isSelected && isHovered && 'shadow-node-hover',
    isHoveredOrSelected && 'shadow-node-active',
  );

  const enableHoverOffset = !!hoverXOffset || !!hoverYOffset;

  const nodeProps = useMemo((): Omit<BaseNodeDisplayProps<TDbtDagNode>, 'children'> => {
    return {
      isHovered,
      isSelected,
      isTransparent,
      disableShadow,
      showOnHover: enableHoverOffset,
      isMagnified: false,
      id,
      data,
      shadowClass,
      hoverStyle: undefined,
    };
  }, [
    isHovered,
    isSelected,
    isTransparent,
    disableShadow,
    enableHoverOffset,
    id,
    data,
    shadowClass,
  ]);

  return useMemo(
    () => (
      <div className="flex items-center justify-center">
        <div
          className={twJoin(
            'pointer-events-none relative h-full cursor-pointer',
            'max-w-[360px]',
            isHovered && 'z-[1002]',
          )}
        >
          {isHovered && (
            <style>
              {`
          .react-flow__node[data-id="${id}"] {
            z-index: 1001 !important;
          }
        `}
            </style>
          )}
          {isSelected && (
            <style>
              {`
          .react-flow__node[data-id="${id}"] {
            z-index: 999 !important;
          }
        `}
            </style>
          )}
          {!disableHandles && (
            <Handle
              type="target"
              position={Position.Left}
              isConnectable={false}
              className="pointer-events-none -left-[1px] cursor-pointer opacity-0"
            />
          )}
          <div
            className="pointer-events-auto relative inline-block w-full"
            onFocusCapture={() => updateIsFocused(true)}
            onBlurCapture={() => updateIsFocused(false)}
            onMouseOver={() => {
              updateIsHovered(true);
            }}
            onMouseLeave={() => {
              updateIsHovered(false);
            }}
            onMouseOut={() => {
              updateIsHovered(false);
            }}
          >
            <BaseNodeDisplay
              {...nodeProps}
              children={children}
              // If there's no offset, then hover in place
              // or if there is an offset, but we're sufficiently zoomed in,
              // Expanded truncated nodes on hover (without scaling)
              showOnHover={!enableHoverOffset || !shouldScaleOffset}
              hoverStyle={
                enableHoverOffset
                  ? undefined
                  : {
                      transform: `scale(${scaleNode})`,
                    }
              }
            />
            {isHovered && shouldMagnify && enableHoverOffset && (
              <BaseNodeDisplay
                {...nodeProps}
                children={children}
                isMagnified
                hoverStyle={
                  shouldScaleOffset
                    ? {
                        pointerEvents: 'none',
                        position: 'absolute',
                        paddingBottom: '16px',
                        width: '20px',
                        top: 0,
                        transformOrigin: 'bottom',
                        transform: `translateX(${hoverXOffset}px) translateY(-${hoverYOffset}px) scale(${scaleNode})`,
                      }
                    : {
                        pointerEvents: 'none',
                        width: '20px',
                        transformOrigin: 'center',
                        transform: `translateX(${hoverXOffset}px) translateY(-${hoverYOffset}px) scale(${scaleNode})`,
                      }
                }
              />
            )}
          </div>
          {!disableHandles && (
            <Handle
              type="source"
              isConnectable={false}
              position={Position.Right}
              className="pointer-events-none absolute -right-1 cursor-pointer opacity-0"
            />
          )}
        </div>
        {data.expandAction && (
          <Button
            className={data.expandAction.className ?? twJoin('ml-2 !p-1')}
            {...data.expandAction}
          ></Button>
        )}
      </div>
    ),
    [
      isHovered,
      id,
      isSelected,
      disableHandles,
      nodeProps,
      children,
      enableHoverOffset,
      shouldScaleOffset,
      scaleNode,
      shouldMagnify,
      hoverXOffset,
      hoverYOffset,
      data,
      updateIsFocused,
      updateIsHovered,
    ],
  );
};

const GetZoomLevel = ({ children }: { children: (zoom: number) => ReactElement }) => {
  const zoom = useZoomLevelDebounced();
  return children(zoom);
};

export const BaseNodeZoomLevel = <TDbtDagNode extends DbtDagNode>(
  props: BaseNodeProps<TDbtDagNode>,
) => {
  const [_isHovered, updateIsHovered] = React.useState(false);
  const [_isFocused, updateIsFocused] = React.useState(false);

  const shouldBeHovered = _isHovered || _isFocused;

  const [isConsideredHovered, setConsideredHovered] = React.useState(false);

  useDebounce(
    () => {
      setConsideredHovered(shouldBeHovered);
    },
    130,
    [shouldBeHovered],
  );

  return useMemo(() => {
    if (isConsideredHovered) {
      return (
        <GetZoomLevel>
          {(zoom) => (
            <BaseNodeContainer
              {...props}
              zoom={zoom}
              isHovered={true}
              isFocused={_isFocused}
              updateIsHovered={updateIsHovered}
              updateIsFocused={updateIsFocused}
            />
          )}
        </GetZoomLevel>
      );
    }
    return (
      <BaseNodeContainer
        {...props}
        zoom={1}
        isHovered={false}
        isFocused={_isFocused}
        updateIsHovered={updateIsHovered}
        updateIsFocused={updateIsFocused}
      />
    );
  }, [_isFocused, isConsideredHovered, props]);
};

export const BaseNodeComponent = React.memo(BaseNodeZoomLevel);
